import { useState, useEffect } from 'react';
import { HeaderShort, HeaderShortProps } from '../../headers';
import { Star, StarBorder } from '@mui/icons-material';
import {
  Toolbar,
  Divider,
  Box,
  Drawer,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  IconButton,
  Typography,
} from '@mui/material';

const drawerWidth = 240;

interface AppBarDrawerProps {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  tabs: any;
  headerProps: HeaderShortProps;
  extras?: any[];
  onMenuChange?: Function;
  initialTab?: string;
  defaultTab?: string;
  config?: {
    appBar: { defaultTab: string };
  };
  onConfigChange?: (tab: string) => void;
  configLoading?: boolean;
}

interface AppBarDrawerPageState {
  tab: string;
}

export const AppBarDrawer = ({
  window,
  tabs,
  headerProps,
  extras,
  onMenuChange,
  initialTab,
  defaultTab,
  onConfigChange,
}: AppBarDrawerProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tab, setTab] = useState(
    initialTab || defaultTab || Object.keys(tabs)[0]
  );
  const [updatedDefaultTab, setUpdatedDefaultTab] = useState(defaultTab);
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  useEffect(() => {
    if (defaultTab && defaultTab !== updatedDefaultTab) {
      setTab(defaultTab);
      setUpdatedDefaultTab(defaultTab);
    }
  }, [defaultTab, tabs]);
  const [hover, setHover] = useState<string>('');
  const container =
    window !== undefined ? () => window().document.body : undefined;
  const drawer = (
    <>
      <Toolbar
        sx={{ backgroundColor: theme => theme.palette.primary.main }}
        disableGutters
      />
      <Divider />
      {[...(Object.values(tabs) || [])]
        .sort((a: any, b: any) => a.sequence - b.sequence)
        ?.map((tabItem: any, index) => (
          <ListItem
            id={`side-menu-tab-${index}`}
            key={tabItem.title}
            disablePadding
            sx={{
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
              backgroundColor: theme =>
                tabItem.index === tab ? theme.palette.divider : 'transparent',
            }}
            onMouseEnter={() => setHover(tabItem.index)}
            onMouseLeave={() => setHover('')}
          >
            <ListItemButton
              onClick={() => {
                setTab(tabItem.index);
                onMenuChange && onMenuChange(tabItem.index);
                handleDrawerToggle();
              }}
              sx={{ pr: 0 }}
            >
              {tabItem.icon && <ListItemIcon>{tabItem.icon}</ListItemIcon>}
              <ListItemText primary={tabItem.title} />
              {((onConfigChange && updatedDefaultTab === tabItem.index) ||
                (onConfigChange && hover === tabItem.index)) && (
                <ListItemIcon sx={{ minWidth: 'unset' }}>
                  <IconButton
                    onClick={e => {
                      // e.preventDefault();
                      // e.stopPropagation();
                      setUpdatedDefaultTab(tabItem.index);
                      onConfigChange(
                        updatedDefaultTab === tabItem.index
                          ? null
                          : tabItem.index
                      );
                    }}
                    color="secondary"
                    edge="end"
                    size="small"
                  >
                    {updatedDefaultTab === tabItem.index ? (
                      <Star fontSize="small" />
                    ) : (
                      <StarBorder fontSize="small" />
                    )}
                  </IconButton>
                </ListItemIcon>
              )}
            </ListItemButton>
          </ListItem>
        ))}
    </>
  );
  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <HeaderShort
        {...headerProps}
        onMenuIconClick={handleDrawerToggle}
        position="fixed"
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          height: '100vh',
          maxHeight: '100vh',
          overflowY: 'scroll',
        }}
      >
        <Toolbar />
        {tabs?.[tab]?.content && <>{tabs[tab].content}</>}
        {extras && extras?.map(extra => extra)}
      </Box>
    </Box>
  );
};
